import classNames from 'classnames'
import { Formik } from 'formik'
import { captureException } from 'marketing-site/lib/sentry'
import { IProps } from 'marketing-site/src/library/elements/Form'
import { Color, getColorTheme, mq } from 'marketing-site/src/library/utils'
import React, { useState } from 'react'
import { Text } from '../../elements/Text'
import useTranslate from '../../hooks/useTranslate'
import { CTALink } from '../CTALink'
import { FormContent } from './FormContent'
import getInitialValues from './utils/getInitialValues'

export const testIds = {
  form: 'test-form-element',
  heading: 'test-form-element-heading',
  submitButton: 'test-form-element-submit-button',
  resetButton: 'test-form-element-reset-button',
}
export const submitButtonSubmittingText = 'Please wait...'

export const Form = React.memo((props: IProps) => {
  const [error, setError] = useState<string | null>(null)
  const { translate } = useTranslate()

  const {
    fields,
    defaults,
    icon,
    submitButtonLabel,
    headingText,
    subheadingText,
    disableSubmitOnEmptyForm,
    customErrorMessage,
    onSubmit,
    backgroundColor = Color.Transparent,
    theme = 'default',
    secondaryCTA,
  } = props

  if (fields.length === 0) {
    captureException('Tried to load form without fields')
    return <p>{translate("We can't load this form.")}</p>
  }

  const themeColors = getColorTheme('default', backgroundColor)
  const wrapperClassNames = classNames('form-element-wrapper', {
    'default-form-theme': theme === 'default',
    'rounded-form-theme': theme === 'rounded',
    'marketo-form-wrapper': theme === 'marketo',
    'has-background': themeColors.backgroundColor !== Color.Transparent,
  })

  return (
    <div className={wrapperClassNames} data-testid={testIds.form}>
      {headingText && (
        <div className="heading" data-testid={testIds.heading}>
          <Text brand="default" size="heading-4-bold">
            {headingText}
          </Text>
        </div>
      )}
      {subheadingText && (
        <p className="subheading">
          <Text brand="default" size="caption">
            {subheadingText}
          </Text>
        </p>
      )}
      {error && (
        <div className="error-message" data-testid="generic-error-message">
          {translate(error)}
        </div>
      )}
      <Formik
        initialValues={getInitialValues(fields, defaults)}
        enableReinitialize={!!defaults}
        onSubmit={async (values) => {
          setError(null)

          const submissionResult = await onSubmit(values)

          if (!submissionResult.success) {
            const errorMessage = submissionResult.errorMessage || 'An unexpected error happened'
            setError(errorMessage)
          }
        }}
        validateOnBlur={false}
      >
        {() => (
          <FormContent
            icon={icon}
            submitButtonLabel={submitButtonLabel}
            disableSubmitOnEmptyForm={disableSubmitOnEmptyForm}
            customErrorMessage={customErrorMessage}
            fields={props.fields}
            showResetButton={props.showResetButton}
            theme={theme}
          />
        )}
      </Formik>
      {secondaryCTA && (
        <div className="secondary-cta">
          <CTALink {...secondaryCTA} />
        </div>
      )}
      <style jsx>{`
        .form-element-wrapper {
          max-width: 575px;
          background-color: ${themeColors.backgroundColor};

          .secondary-cta {
            padding-top: 1rem;
            :global(.cta-link) {
              width: 100%;
            }
          }

          &.has-background {
            padding: 40px;
          }

          &.rounded-form-theme {
            border-radius: 30px;
            max-width: none;

            :global(.form:not(:first-child)) {
              padding-top: 20px;
            }

            :global(input),
            :global(select),
            :global(textarea),
            :global(.number-input),
            :global(.radio-button-group label) {
              border-radius: 30px;
            }

            :global(.radio-button-group-label) {
              font-size: 14px;
              text-align: center;
            }

            :global(.submit-button) {
              width: 100%;
              margin-top: 0;
            }
          }

          .heading {
            margin-bottom: 16px;
          }

          .error-message {
            display: block;
            width: 100%;
            text-align: center;
            background-color: ${Color.UIError};
            border-radius: 30px;
            margin-bottom: 16px;
            padding: 8px;
          }

          :global(.button-wrapper) {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 18px;
          }

          :global(.submit-button) {
            background-color: ${Color.Blue};
            border: 2px solid ${Color.Blue};
            border-radius: 30px;
            margin: 1em auto 0;
            padding: 12px 25px;
            cursor: pointer;
            color: ${Color.White};
            font-family: $fontTTNormsPro;
            font-weight: bold;
            transition: background-color 500ms, color 500ms;
            width: 100%;
            text-align: center;

            :global(.submit-button-arrow) {
              margin-left: 6px;
            }

            :global(.submit-button-icon) {
              margin-left: 6px;
              vertical-align: middle;
            }

            &:hover,
            &:focus {
              background-color: ${Color.White};
              color: ${Color.Blue};

              :global(.submit-button-arrow) {
                transform: translateX(4px);
              }
            }
          }

          :global(.reset-button) {
            font-family: $fontTTNormsPro;
            font-weight: bold;
            text-decoration: underline;
            text-decoration-thickness: 2px;
            text-underline-offset: 5px;
            font-size: 15px;
            cursor: pointer;
          }

          @media (${mq.laptop}) {
            &.has-background {
              padding: 70px 70px 40px 70px;
            }

            :global(.button-wrapper) {
              flex-direction: row-reverse;
              align-items: baseline;
              gap: 40px;

              .reset-button {
                padding-left: 10px;
              }
            }
          }
        }
      `}</style>
      <style jsx global>
        {`
          :root {
            --form-label-spacing-y: 14px;
          }
        `}
      </style>
    </div>
  )
})
